import React, { useState } from "react";

export default function LoanOfferPage({ setCurrentStep, selectedOffer }) {
  // 👇 Hardcoded for frontend flow until the API is integrated
  const maxAmount = 200000; 
  const minAmount = 10000;

  const [loanAmount, setLoanAmount] = useState(50000); // Default to 50k as per mockup
  const [tenure, setTenure] = useState("24m");

  // Format numbers to Indian Rupee format (e.g., 2,00,000)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN').format(amount);
  };

  // Handle manual input in the text box (automatically caps at maxAmount)
  const handleAmountInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value === "") {
      setLoanAmount(minAmount);
      return;
    }
    const numValue = Number(value);
    if (numValue <= maxAmount) {
      setLoanAmount(numValue);
    } else {
      setLoanAmount(maxAmount);
    }
  };

  // Calculate the percentage for the green slider track fill
  const sliderProgress = ((loanAmount - minAmount) / (maxAmount - minAmount)) * 100;

  const handleSubmit = () => {
    console.log("Offer Accepted:", { loanAmount, tenure });
    // Move to next step (e.g., Step 13: KYC or Final Review)
    setCurrentStep(13); 
  };

  const lenderName = selectedOffer?.lender_name || "CredFin";
  const tenures = ["3m", "6m", "9m", "12m", "18m", "24m", "30m", "36m"];

  return (
    <div className="w-full max-w-[400px] mx-auto bg-white min-h-[100dvh] font-atkinson flex flex-col relative pb-[130px]">
      
      {/* Header & Progress Bar */}
      <div className="px-5 pt-6 pb-4 border-b border-transparent">
        <div className="flex justify-center mb-4">
          <div className="text-center leading-none">
            <h1 className="text-[#205A6D] font-bold text-[22px] tracking-tight">
              {lenderName}
            </h1>
            <p className="text-[#7ea4b1] text-[9px] font-semibold tracking-widest mt-1 uppercase">Loans made Easy</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <button onClick={() => setCurrentStep(11)} className="w-6 h-6 flex items-center justify-start text-gray-700">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          
          <div className="flex-1 mx-4 h-[4px] bg-[#e6f4ed] rounded-full overflow-hidden">
            <div className="h-full bg-[#12f280] w-[78%] rounded-full"></div>
          </div>
          
          <span className="text-[12px] text-gray-500 font-medium">78%</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-5 pt-2 flex-1">
        <h2 className="text-[22px] font-bold text-[#1b1b1b]">Your loan offer</h2>
        <p className="text-[13px] text-[#8b8b8b] mt-2 mb-6 leading-relaxed pr-6">
          Pick amount and tenure. EMI updates instantly.
        </p>

        {/* Congratulations Box */}
        <div className="bg-[#EBFBF4] border border-[#D1EFE2] rounded-[16px] p-5 mb-8">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="font-bold text-[15px] text-[#1b1b1b]">Congratulations</span>
            <span>🎉</span>
          </div>
          <p className="text-[12px] text-[#1b1b1b] font-medium mb-4">
            You're eligible for a {lenderName} Personal Loan
          </p>
          
          <hr className="border-[#D1EFE2] mb-4" />
          
          <div className="text-[24px] font-bold text-[#1b1b1b]">
            Upto ₹{formatCurrency(maxAmount)}
          </div>
        </div>

        {/* Amount Modification Section */}
        <div className="mb-8">
          <div className="border border-[#e6e6e6] rounded-[14px] px-4 py-2.5 mb-6 focus-within:border-black transition-colors">
            <label className="text-[11px] text-[#8b8b8b] block mb-0.5">Modify loan amount</label>
            <div className="flex items-center text-[16px] font-bold text-[#1b1b1b]">
              <span className="mr-1">₹</span>
              <input 
                type="text" 
                value={formatCurrency(loanAmount)}
                onChange={handleAmountInputChange}
                className="w-full outline-none bg-transparent"
              />
            </div>
          </div>

          {/* Custom Range Slider */}
          <div className="relative w-full h-1.5 rounded-full bg-[#e6e6e6] mb-8">
            <input
              type="range"
              min={minAmount}
              max={maxAmount}
              step="5000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(Number(e.target.value))}
              className="absolute top-0 w-full h-full appearance-none bg-transparent cursor-pointer z-10"
              style={{ WebkitAppearance: "none" }}
            />
            {/* The Green Fill Track */}
            <div 
              className="absolute top-0 left-0 h-full bg-[#00C676] rounded-full pointer-events-none"
              style={{ width: `${sliderProgress}%` }}
            ></div>
            {/* The Green Thumb visual */}
            <div 
              className="absolute top-1/2 w-4 h-4 bg-[#00C676] rounded-full transform -translate-y-1/2 -translate-x-1/2 pointer-events-none shadow-sm"
              style={{ left: `${sliderProgress}%` }}
            ></div>
          </div>

          <hr className="border-[#f0f0f0]" />
        </div>

        {/* Tenure Selection Section */}
        <div>
          <label className="text-[12px] text-[#555] block mb-3 font-medium">Select Tenure</label>
          <div className="grid grid-cols-4 gap-3">
            {tenures.map((t) => (
              <button
                key={t}
                onClick={() => setTenure(t)}
                className={`py-2.5 rounded-[12px] text-[13px] font-bold transition-colors border ${
                  tenure === t 
                    ? "bg-black text-white border-black" 
                    : "bg-white text-[#8b8b8b] border-[#e6e6e6] hover:border-[#cccccc]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sticky Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 z-50 mx-auto w-full max-w-[400px] bg-white px-5 py-4 border-t border-transparent">
        <button 
          onClick={handleSubmit} 
          className="w-full bg-black text-white font-bold text-[16px] rounded-[14px] py-[18px] active:scale-[0.98] transition-transform"
        >
          Accept & Continue
        </button>
      </div>

      {/* Global styles for hiding the default browser slider thumb so our custom one shows perfectly */}
      <style dangerouslySetInnerHTML={{__html: `
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: transparent;
          cursor: pointer;
        }
        input[type=range]::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border: none;
          border-radius: 50%;
          background: transparent;
          cursor: pointer;
        }
      `}} />
    </div>
  );
}